import { AxiosInstance } from 'axios';

import { accessEndpoints } from '../../../constants/endpoints';
import { PaginationRequest } from '../../../types';
import {
  CreateRoleDto,
  IAccess,
  IPaginatedRolesResponse,
  IPaginatedRoleUsersResponse,
  IRole,
  IRoleListItem,
  UpdateRoleDto,
} from '../schemas/access.schema';

export interface CreateRoleResponse {
  message: string;
  data: {
    role: IRole;
    userRoleId: number;
  };
}

/** Response of `PATCH /api/access/role/:id`. */
export interface UpdateRoleResponse {
  message: string;
  data: IRole;
}

/** Response of management endpoints (`add-users`, `remove-users`, `DELETE`). */
export interface AccessMessageResponse {
  message: string;
}

export class AccessApi {
  #requestInstance: AxiosInstance;

  constructor(requestInstance: AxiosInstance) {
    this.#requestInstance = requestInstance;
  }

  /** `GET /api/access` — full catalogue of permissions (with `group`). */
  getAccesses = async (): Promise<IAccess[]> =>
    this.#requestInstance.get(accessEndpoints.getAccesses) as unknown as Promise<IAccess[]>;

  /** `GET /api/access/roles` — paginated list of roles with `userCount`. */
  getRoles = async (params?: PaginationRequest): Promise<IPaginatedRolesResponse> =>
    this.#requestInstance.get(accessEndpoints.getRoles, {
      params,
    }) as unknown as Promise<IPaginatedRolesResponse>;

  /** `GET /api/access/roles/all` — lightweight list of all roles (no pagination). */
  getRolesAll = async (): Promise<IRoleListItem[]> =>
    this.#requestInstance.get(accessEndpoints.getRolesAll) as unknown as Promise<IRoleListItem[]>;

  /** `GET /api/access/role/:id` — full role with `roleAccesses` + `userRoles`. */
  getRoleById = async (id: number): Promise<IRole> =>
    this.#requestInstance.get(accessEndpoints.getRoleById(id)) as unknown as Promise<IRole>;

  /** `GET /api/access/role/:id/users` — paginated list of users in a role. */
  getRoleUsers = async (
    id: number,
    params?: PaginationRequest
  ): Promise<IPaginatedRoleUsersResponse> =>
    this.#requestInstance.get(accessEndpoints.getRoleUsers(id), {
      params,
    }) as unknown as Promise<IPaginatedRoleUsersResponse>;

  /**
   * `POST /api/access/create-role` — create a role with accesses and an
   * optional initial set of users (single backend transaction).
   */
  createRole = async (dto: CreateRoleDto): Promise<CreateRoleResponse> =>
    this.#requestInstance.post(
      accessEndpoints.createRole,
      dto
    ) as unknown as Promise<CreateRoleResponse>;

  /** `PATCH /api/access/role/:id` — update name / description / accesses. */
  updateRole = async (id: number, dto: UpdateRoleDto): Promise<UpdateRoleResponse> =>
    this.#requestInstance.patch(
      accessEndpoints.updateRole(id),
      dto
    ) as unknown as Promise<UpdateRoleResponse>;

  /** `DELETE /api/access/role/:id`*/
  deleteRole = async (id: number): Promise<AccessMessageResponse> =>
    this.#requestInstance.delete(
      accessEndpoints.deleteRole(id)
    ) as unknown as Promise<AccessMessageResponse>;

  /** `PATCH /api/access/role/:id/add-users`.*/
  addUsersToRole = async (roleId: number, userIds: number[]): Promise<AccessMessageResponse> =>
    this.#requestInstance.patch(accessEndpoints.addUsersToRole(roleId), {
      userIds,
    }) as unknown as Promise<AccessMessageResponse>;

  /** `PATCH /api/access/role/:id/remove-users`. Same body shape as `add-users`. */
  removeUsersFromRole = async (roleId: number, userIds: number[]): Promise<AccessMessageResponse> =>
    this.#requestInstance.patch(accessEndpoints.removeUsersFromRole(roleId), {
      userIds,
    }) as unknown as Promise<AccessMessageResponse>;

  /** `POST /api/access/role/:id/duplicate` — clone a role without its users. */
  duplicateRole = async (id: number): Promise<CreateRoleResponse> =>
    this.#requestInstance.post(
      accessEndpoints.duplicateRole(id)
    ) as unknown as Promise<CreateRoleResponse>;

  /**
   * `POST /api/access/role/:id/delete-with-reassignment` — transactionally
   * reassign all listed users to their new roles and delete the old role.
   */
  deleteRoleWithReassignment = async (
    roleId: number,
    reassignments: { userId: number; newRoleId: number }[]
  ): Promise<AccessMessageResponse> =>
    this.#requestInstance.post(accessEndpoints.deleteRoleWithReassignment(roleId), {
      reassignments,
    }) as unknown as Promise<AccessMessageResponse>;
}
